import { z } from "zod";
import { kibbePreviewSchema } from "./kibbe";

export const KIBBE_CHECK_ERRORS = ["invalid_image", "invalid_input", "rate_limit", "unsuitable", "timeout", "network", "http", "response", "origin", "provider", "schema", "refusal", "incomplete"] as const;
export type KibbeCheckCategory = typeof KIBBE_CHECK_ERRORS[number];
export const kibbeSuitabilitySchema = z.object({ suitable: z.boolean(), reason: z.string().max(500) });
export const kibbePhotoResponseSchema = z.union([
  z.object({ suitable: z.literal(false), reason: z.string().trim().min(1).max(500) }),
  kibbePreviewSchema.refine(value => value.primaryType !== value.secondaryType),
]);
export function kibbeHttpCategory(status: number, code?: unknown): KibbeCheckCategory {
  if (status === 429) return "rate_limit";
  if (status === 408 || status === 504) return "timeout";
  return typeof code === "string" && KIBBE_CHECK_ERRORS.includes(code as KibbeCheckCategory) ? code as KibbeCheckCategory : "http";
}

// Only trusted deployment configuration can add a host. Never use request Host headers.
export function kibbeOriginAllowed(origin: string | null, deploymentHost?: string): boolean {
  if (!origin) return true;
  const trustedHost = deploymentHost?.toLowerCase();
  const allowed = ["coloranalysistech.com", "www.coloranalysistech.com", "localhost", "127.0.0.1"];
  if (trustedHost && /^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/.test(trustedHost)) allowed.push(trustedHost);
  try {
    const url = new URL(origin);
    return ["http:", "https:"].includes(url.protocol) && !url.username && !url.password && url.pathname === "/" && !url.search && !url.hash && allowed.includes(url.hostname);
  } catch { return false; }
}

type ModelResponse = { choices?: Array<{ finish_reason?: string | null; message?: { refusal?: string | null; content?: string | null } }> };
export function parseKibbeModelResponse(response: ModelResponse, suitabilityOnly: boolean) {
  const choice = response.choices?.[0];
  if (choice?.message?.refusal) return { ok: false as const, code: "refusal" as const };
  if (choice?.finish_reason !== "stop") return { ok: false as const, code: "incomplete" as const, finishReason: choice?.finish_reason === "length" ? "length" : choice?.finish_reason === "content_filter" ? "content_filter" : !choice ? "missing" : "other" };
  let raw;
  try { raw = JSON.parse(choice.message?.content || ""); } catch { return { ok: false as const, code: "schema" as const }; }
  const parsed = (suitabilityOnly ? kibbeSuitabilitySchema : kibbePhotoResponseSchema).safeParse(raw);
  if (!parsed.success || (!parsed.data.suitable && !parsed.data.reason?.trim())) return { ok: false as const, code: "schema" as const };
  return { ok: true as const, result: parsed.data };
}
